import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { PlexusCanvas } from "@/components/site/PlexusCanvas";
import { ToothIcon } from "@/components/site/ToothIcon";
import {
  Scale,
  Hammer,
  HeartPulse,
  Building2,
  Calculator,
} from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const INDUSTRIES = [
  {
    icon: ToothIcon,
    title: "Zahnarztpraxen",
    text: "Automatische Terminerinnerungen und Recall-Management — die Praxis ruft an, bevor der Patient es vergisst.",
    metric: "−38 % Terminausfälle",
  },
  {
    icon: Scale,
    title: "Kanzleien",
    text: "KI-gestützte Dokumentenvorbereitung und Mandanten-Erstkontakt — Routinearbeit verschwindet, Beratung bleibt.",
    metric: "+11 Std. pro Woche",
  },
  {
    icon: Hammer,
    title: "Handwerksbetriebe",
    text: "Angebote, Rechnungen und Materialbestellungen automatisiert — direkt von der Baustelle aus dem Handy.",
    metric: "2× schnellere Angebote",
  },
  {
    icon: HeartPulse,
    title: "Kliniken & MVZ",
    text: "Patientenkommunikation entlastet: Aufklärungsbögen, Nachsorge-Infos und Terminkoordination laufen automatisch.",
    metric: "−45 % Telefonaufkommen",
  },
  {
    icon: Building2,
    title: "Immobilien",
    text: "Exposés in Minuten statt Stunden, Interessenten-Anfragen rund um die Uhr qualifiziert und beantwortet.",
    metric: "24/7 Anfragenbearbeitung",
  },
  {
    icon: Calculator,
    title: "Steuerkanzleien",
    text: "Belegerfassung mit KI: Dokumente werden erkannt, zugeordnet und vorkontiert — Ihr Team prüft nur noch.",
    metric: "−60 % manuelle Eingaben",
  },
];

export const Industries = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".industry-card",
        { opacity: 0, scale: 0.94 },
        {
          opacity: 1,
          scale: 1,
          duration: 0.85,
          ease: "power3.out",
          stagger: 0.1,
          scrollTrigger: { trigger: ".industry-grid", start: "top 80%" },
        }
      );

      // Staggered column parallax (desktop)
      if (!reduced && window.innerWidth >= 1024) {
        const speeds = [26, 72, 44];
        gsap.utils.toArray(".industry-card").forEach((card, i) => {
          const s = speeds[i % 3];
          gsap.fromTo(
            card,
            { y: s },
            {
              y: -s,
              ease: "none",
              scrollTrigger: {
                trigger: ".industry-grid",
                start: "top bottom",
                end: "bottom top",
                scrub: true,
              },
            }
          );
        });
      }
      gsap.fromTo(
        ".industry-header > *",
        { y: reduced ? 0 : 36, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: "power3.out",
          stagger: 0.12,
          scrollTrigger: { trigger: ".industry-header", start: "top 85%" },
        }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="branchen"
      data-section-idx="3"
      data-dark="true"
      className="relative"
      style={{ backgroundColor: "#1E1B18" }}
      data-testid="industries-section"
    >
      <div
        className="sticky top-0 h-screen w-full overflow-hidden"
        style={{ background: "radial-gradient(ellipse 90% 70% at 50% 38%, #2B2825 0%, #1E1B18 70%)" }}
      >
        <PlexusCanvas />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#1E1B18]/70 via-transparent to-[#1E1B18]/80" />
      </div>

      <div className="relative z-10 -mt-[100vh] min-h-screen pb-32 pt-28 lg:pb-40 lg:pt-36">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="industry-header max-w-3xl">
            <p className="eyebrow mb-6 text-[#E8C887]/80">004 / 006 — Case</p>
            <h2 className="font-display text-4xl text-[#F5F1E8] sm:text-5xl lg:text-6xl">
              Branchen, die wir kennen
            </h2>
            <p className="mt-6 max-w-xl text-base text-[#F5F1E8]/65 md:text-lg">
              Wir arbeiten dort, wo Zeit am knappsten ist — und Verwaltung am
              meisten kostet.
            </p>
          </div>

          <div className="industry-grid mt-16 grid gap-7 sm:grid-cols-2 lg:mt-20 lg:grid-cols-3">
            {INDUSTRIES.map((industry, i) => (
              <article
                key={industry.title}
                className="industry-card glass-card p-8"
                data-testid={`industry-card-${i}`}
              >
                <industry.icon size={26} strokeWidth={1.6} className="text-[#E8C887]" />
                <h3 className="font-display mt-6 text-xl text-[#F5F1E8] sm:text-2xl">
                  {industry.title}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-[#F5F1E8]/60 sm:text-base">
                  {industry.text}
                </p>
                <p className="font-serif-accent mt-6 text-lg text-[#E8C887]">
                  {industry.metric}
                </p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
